import React, { useState, useMemo } from 'react';
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { Player, PlayerWithLeagueContext, LeagueOutput } from '../../../generated-api';
import { Team } from '../../SortableEntity';
import PlayerBubble, { NewPlayerBubble } from '../PlayerBubble';

interface PlayerSelectionStepProps {
  players: (Player | PlayerWithLeagueContext)[];
  selectedPlayerIds: string[];
  teams: Team[];
  recentPlayerIds: string[];
  currentPlayerId?: string | null;
  leagues: LeagueOutput[];
  onTogglePlayer: (playerId: string) => void;
  onCycleTeam: (playerId: string) => void;
  onNewPlayer: () => void;
}

const PlayerSelectionStep: React.FC<PlayerSelectionStepProps> = ({
  players,
  selectedPlayerIds,
  teams,
  recentPlayerIds,
  currentPlayerId,
  leagues,
  onTogglePlayer,
  onCycleTeam,
  onNewPlayer,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterRecent, setFilterRecent] = useState(false);
  const [filterLeagueId, setFilterLeagueId] = useState<string | null>(null);

  const hasActiveFilters = filterRecent || filterLeagueId !== null;

  const getPlayerTeamColor = (playerId: string): string | undefined => {
    const team = teams.find(t => t.playerIds.includes(playerId));
    return team?.color;
  };

  // Only show non-system, non-game-specific leagues as filter options
  const filterableLeagues = useMemo(() =>
    leagues.filter(l => !l.isSystemLeague && !l.isGameSpecificLeague),
    [leagues]
  );

  // Stable sort: does NOT depend on selectedPlayerIds
  // Current player first, then recent players in recency order, then alphabetical
  const stableSortedPlayers = useMemo(() => {
    return [...players].sort((a, b) => {
      if (a.id === currentPlayerId) return -1;
      if (b.id === currentPlayerId) return 1;
      const aRecent = recentPlayerIds.indexOf(a.id);
      const bRecent = recentPlayerIds.indexOf(b.id);
      const aRecentScore = aRecent >= 0 ? aRecent : 999;
      const bRecentScore = bRecent >= 0 ? bRecent : 999;
      if (aRecentScore !== bRecentScore) return aRecentScore - bRecentScore;
      return a.name.localeCompare(b.name);
    });
  }, [players, recentPlayerIds, currentPlayerId]);

  // Apply search + filters on top of stable sort
  const filteredPlayers = useMemo(() => {
    let result = stableSortedPlayers;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q));
    }
    if (filterRecent) {
      result = result.filter(p => recentPlayerIds.includes(p.id));
    }
    if (filterLeagueId) {
      const league = leagues.find(l => l.id === filterLeagueId);
      if (league) {
        result = result.filter(p => league.playerIds.includes(p.id));
      }
    }
    return result;
  }, [stableSortedPlayers, searchQuery, filterRecent, filterLeagueId, recentPlayerIds, leagues]);

  const hasSearchOrFilter = searchQuery || hasActiveFilters;

  const resetFilters = () => {
    setFilterRecent(false);
    setFilterLeagueId(null);
    setShowFilters(false);
  };

  return (
    <div className="flex flex-col">
      {/* 1. Search + filter bar */}
      <div className="shrink-0 mb-3 pt-2">
        <div className="flex gap-2">
          {/* Search input */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-4 w-4 text-disabled" />
            </div>
            <input
              type="text"
              placeholder="Search players..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="block w-full pl-9 pr-3 py-2.5 border border-edge-input rounded-lg
                         text-sm focus:ring-primary-500 focus:border-primary-500 bg-surface"
            />
          </div>

          {/* Filter button */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`relative shrink-0 w-11 h-11 flex items-center justify-center rounded-lg border transition-colors
                        ${hasActiveFilters
                          ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:border-primary-600'
                          : 'bg-surface border-edge-input text-body hover:bg-surface-hover'
                        }`}
            aria-label="Toggle filters"
            aria-expanded={showFilters}
          >
            <FunnelIcon className="h-4 w-4" />
            {hasActiveFilters && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary-500" />
            )}
          </button>
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="mt-3 flex flex-wrap gap-2">
            {/* Recent filter chip */}
            <button
              type="button"
              onClick={() => setFilterRecent(!filterRecent)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors
                          ${filterRecent
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-surface border-edge text-body hover:border-edge-input'
                          }`}
            >
              Recent
            </button>

            {/* League filter chips */}
            {filterableLeagues.map(league => {
              const isActive = filterLeagueId === league.id;
              return (
                <button
                  data-track="wizard_player_filter_league"
                  key={league.id}
                  type="button"
                  onClick={() => setFilterLeagueId(isActive ? null : league.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors
                              ${isActive
                                ? 'bg-primary-500 text-white border-primary-500'
                                : 'bg-surface border-edge text-body hover:border-edge-input'
                              }`}
                >
                  {league.name}
                </button>
              );
            })}

            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {/* 3. Player grid (scrollable) */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {filteredPlayers.length === 0 && !hasSearchOrFilter ? (
          <div className="text-center py-12">
            <span className="text-4xl block mb-3">👥</span>
            <h3 className="text-base font-medium text-heading mb-1">No players yet</h3>
            <p className="text-sm text-muted">Create your first player to get started</p>
          </div>
        ) : filteredPlayers.length === 0 && hasSearchOrFilter ? (
          <div className="text-center py-12">
            <MagnifyingGlassIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-base font-medium text-heading mb-1">No players found</h3>
            <p className="text-sm text-muted">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-x-2 gap-y-3 pb-2 pl-2 pt-2">
            {filteredPlayers.map(player => (
              <PlayerBubble
                key={player.id}
                player={player}
                isSelected={selectedPlayerIds.includes(player.id)}
                isLocked={player.id === currentPlayerId}
                teamColor={getPlayerTeamColor(player.id)}
                onToggle={onTogglePlayer}
              />
            ))}
            <NewPlayerBubble onClick={onNewPlayer} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerSelectionStep;
